import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

import { TOGGLE_RIGHT_DRAWER_EVENT } from '../ThemeBase';

/**
  * @typedef {Object} SearchGroup
  * @param {string} name
  * @param {any[]} results
  */

@Component({
  template: '#wetheme-right-drawer-template',
})
class RightDrawer extends Vue {
  /** @type {boolean} */
  isOpen = false;

  /** @type {'search' | 'shop-now' | 'page'} */
  type = 'search';

  /** @type {number | null} */
  lineQuantityUpdating = null;

  /** @type {string | null} */
  quickShopProductUrl = null;

  /** @type {string} */
  quickShopContent = '';

  /** @type {boolean} */
  searching = false;

  /** @type {string} */
  searchQuery = ''

  /** @type {SearchGroup[]} */
  searchGroups = [];

  /** @type {number | null} */
  searchChangeTimer = null;

  /** @type {string} */
  searchSummary = '';

  /** @type {boolean} */
  searchHasResults = false

  /** @type {number} */
  searchResultsNum = 0;

  /** @type {string} */
  @Prop({required: true})
  searchUrl;

  /** @type {string} */
  @Prop({required: false})
  isPredictive;

  // /** @type {string} */
  @Prop({required: false})
  languageUrl;

  data() {
    return {
      isOpen: false,
      type: 'search',
      lineQuantityUpdating: null,
      quickShopProductUrl: null,
      quickShopContent: '',
      searchQuery: '',
      searchResults: []
    }
  }

  @Watch('searchQuery')
  onSearchChange() {
    if(this.isSearchPredictive) {
      if(this.searchQuery > '') {
        if (this.searchChangeTimer) {
          window.clearTimeout(this.searchChangeTimer);
        }
        this.searchChangeTimer = window.setTimeout(() => {
          this.performSearch();
          this.searchChangeTimer = null;
        }, 250);
      }
      else {
        this.searchResultsNum = 0;
        this.searchHasResults = false;
        this.searchGroups = [];
        this.searching = false;
        this.searchSummary = '';
      }
    }
  }

  /** @returns {import('../ThemeBase').default} */
  get theme() {
    // @ts-ignore
    return this.$root.theme;
  }

  mounted() {
    document.documentElement.addEventListener(TOGGLE_RIGHT_DRAWER_EVENT, this.handleToggle);
    // A custom event to toggle the right drawer from within web components
    window.eventBus.on('toggle:right:drawer', this.handleToggle);
    window.eventBus.on('close:right:drawer', this.close);
  }

  beforeDestroy() {
    document.documentElement.removeEventListener(TOGGLE_RIGHT_DRAWER_EVENT, this.handleToggle);
    window.eventBus.off('toggle:right:drawer', this.handleToggle);
    window.eventBus.on('close:right:drawer', this.close);
  }

  handleToggle(event) {
    const { type, forceOpen, params } = event.detail ? event.detail : event;
    if (params) {
      if (params.url) {
        const url = params.url;
        if(type === 'shop-now') {
          if (this.quickShopProductUrl !== url) {
            this.getProductPage(url);
            this.quickShopProductId = url;
          }
        } else if (type === 'page') {
          this.getGenericPage(url);
        }
      }
    }

    this.toggle(type, forceOpen);
  }

  close() {
    document.body.classList.remove('js-drawer-open');
    document.body.classList.remove('js-drawer-open-right');
    this.isOpen = false;
  }

  open() {
    document.body.classList.add('js-drawer-open');
    document.body.classList.add('js-drawer-open-right');
    this.isOpen = true;
    if (this.type === 'search') {
      this.$nextTick(() => ((/** @type {HTMLElement} */ (this.$refs.searchText)).focus()));
    }
  }

  toggle(type, forceOpen = undefined) {
    if (forceOpen === true) {
      this.type = type;
      this.open();
    } else if (forceOpen === false) {
      this.close();
    } else if (this.isOpen) {
      this.close();
    } else {
      this.type = type;
      this.open();
    }
  }

  /**
   * Fetch page
   *
   * @param {string} url
   */
  async fetchPage(url) {
    const response = await window.fetch(url);
    const parser = new DOMParser();
    const doc = parser.parseFromString(await response.text(), 'text/html');
    const pageContent = doc.querySelector('.page-main--wrapper');
    return pageContent;
  }

  /**
   * Product page url
   *
   * @param {string} url
   */
  async getProductPage(url) {
    try {
      const pageContent = await this.fetchPage(url);
      if (!pageContent) {
        console.error("Fetched page content is undefined or null.");
        return;
      }
  
      const template = pageContent.querySelector('template[data-quick-view-product]');
      if (!template || !template.content) {
        console.error("Template or template content not found.");
        return;
      }
  
      const productDiv = template.content;
  
      // Ensure the target element exists
      const target = this.$refs.shopNowContent;
      if (!(target instanceof HTMLElement)) {
        console.error("Target element is not an HTMLElement.");
        return;
      }
  
      // Append child only if productDiv is a valid node
      if (productDiv instanceof Node) {
        target.appendChild(productDiv);
      } else {
        console.error("productDiv is not a valid Node.");
        return;
      }
  
      // window.Shopify.PaymentButton does not exist until the fetched
      // product page is fully loaded
      setTimeout(() => {
        if (window.Shopify && window.Shopify.PaymentButton) {
          window.Shopify.PaymentButton.init();
        }
      }, 1250);
    } catch (error) {
      console.error("Error in getProductPage:", error);
    }
  }  

  /**
   * Page url
   *
   * @param {string} url
   */
  async getGenericPage(url) {
    const pageContent = await this.fetchPage(url);
    const target = /** @type {HTMLElement} */ (this.$refs.pageContent);
    target.appendChild(pageContent);
  }

  onSearchSubmit() {
    window.location.href = this.searchPageUrlWithQuery;
    return;
  }

  clearSearchInput() {
    this.searchQuery = '';
    (/** @type {HTMLElement} */ (this.$refs.searchText)).focus();
  }

  get isSearchPredictive() {
    return (this.isPredictive == 'true');
  }

  get searchUrlWithQuery() {
    if(this.isSearchPredictive) {
      return`${this.searchUrl}?q=${encodeURIComponent(this.searchQuery)}&resources[limit]=3&resources[limit_scope]=each`;
    }
    else {
      return`/search?q=${encodeURIComponent(this.searchQuery)}`;
    }
  }

  get searchPageUrlWithQuery() {
    const languageParam = !this.languageUrl || this.languageUrl == '/' ? '' : this.languageUrl;
    return`${languageParam}/search?q=${encodeURIComponent(this.searchQuery)}*`;
  }

  async performSearch() {
    this.searching = true;
    this.searchHasResults = false;
    this.searchResultsNum = 0;
    this.searchSummary = '';
    let data;
    try {
      const response = await fetch(`${this.searchUrlWithQuery}`);

      if (!response.ok) {
        // FIXME handle error
      }
      data = await response.json();

    } catch (e) {
      console.error("Search failed", e);
      // We'll redirect the user to /search page when search fails (for whatever reason)
      window.location.href = this.searchPageUrlWithQuery;
    }

    const resultGroups = new Map();

    // Loop over groups e.g. Products / Pages / Articles / Collections
    Object.keys(data.resources.results).forEach((resultType) => {
      let group = resultGroups.get(resultType);
      let result = data.resources.results[resultType];

      if (!group) {
        group = {
          name: {
            'queries': this.theme.translations.suggestions,
            'articles': this.theme.translations.articles,
            'pages': this.theme.translations.pages,
            'products': this.theme.translations.products,
            'collections': this.theme.translations.collections,
          }[resultType],
          results: [],
        };
        resultGroups.set(resultType, group);
      }

      // Format price.
      let resultWithPrice = result.map(item => {
        var temp = Object.assign({}, item);
        const pageContainer = document.getElementById('PageContainer');
        let showCurrencyCode = pageContainer.dataset.showCurrencyCode;
        let currencyCode = pageContainer.dataset.currencyCode;

        if(temp.price) {
          if (currencyCode && showCurrencyCode == 'true') {
            temp.price = `${this.theme.formatMoney(temp.price)} ${currencyCode}`;
          } else {
            temp.price = this.theme.formatMoney(temp.price);
          }
        }
        return temp;
      });

      group.results = resultWithPrice;

      if(group.results && group.results.length > 0) {
        this.searchResultsNum += result.length;
        this.searchHasResults = true;
      }
    });

    if (!this.searchHasResults && this.searchQuery > '') {
      this.searchSummary = this.theme.translations.no_results_html.replace('{{ terms }}', this.searchQuery);
    }
    this.searching = false;
    this.searchGroups = Array.from(resultGroups.values());
  }
}

export default RightDrawer;
